import React, { useState, useRef, useEffect } from 'react';
import s3Service from '../services/s3Service';

const S3ImageUpload = ({ 
  onImageUpload, 
  onImageChange, 
  currentImage = null, 
  accept = "image/*", 
  maxSize = 100 * 1024 * 1024, // 100MB default
  className = "form-control",
  helpText = "The photograph must be a clear headshot of true likeness of yourself. Allowed formats: JPEG, PNG, GIF, WebP, BMP. Maximum file size: 100MB",
  showPreview = true,
  previewClassName = "img-responsive mb-20",
  exampleImage = "/images/photograph-woman.jpg",
  folder = "uploads/images/users" // Default folder for user images (matches Laravel config)
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Process current image to get CDN URL
  useEffect(() => {
    const processCurrentImage = async () => {
      console.log('S3ImageUpload: Processing currentImage:', currentImage);
      
      if (!currentImage) {
        console.log('S3ImageUpload: No currentImage, setting to null');
        setCurrentImageUrl(null);
        return;
      }

      try {
        // If it's already a full URL (http/https), use it directly
        if (typeof currentImage === 'string' && currentImage.startsWith('http')) {
          console.log('S3ImageUpload: Using full URL directly:', currentImage);
          setCurrentImageUrl(currentImage);
          return;
        }

        // If it's a File object, create object URL for preview
        if (currentImage instanceof File) {
          console.log('S3ImageUpload: Creating object URL for File');
          const objectUrl = URL.createObjectURL(currentImage);
          setCurrentImageUrl(objectUrl);
          return;
        }

        // If it's an S3 key, convert to CDN URL
        if (typeof currentImage === 'string') {
          console.log('S3ImageUpload: Processing S3 key:', currentImage);
          // Try to get URL from backend first (with CDN support)
          try {
            const url = await s3Service.getFileUrlFromBackend(currentImage);
            console.log('S3ImageUpload: Got URL from backend:', url);
            setCurrentImageUrl(url);
          } catch (err) {
            console.log('S3ImageUpload: Backend failed, using local construction');
            // Fallback to local URL construction
            const url = s3Service.getFileUrl(currentImage);
            console.log('S3ImageUpload: Local URL construction:', url);
            setCurrentImageUrl(url);
          }
          return;
        }

        // If it's an object with image property, try to extract URL
        if (currentImage && typeof currentImage === 'object' && currentImage.image) {
          console.log('S3ImageUpload: Processing object with image property:', currentImage);
          const imageValue = currentImage.image;
          
          if (typeof imageValue === 'string' && imageValue.startsWith('http')) {
            console.log('S3ImageUpload: Using image property URL directly:', imageValue);
            setCurrentImageUrl(imageValue);
          } else if (typeof imageValue === 'string') {
            console.log('S3ImageUpload: Processing image property as S3 key:', imageValue);
            // Try to get URL from backend first (with CDN support)
            try {
              const url = await s3Service.getFileUrlFromBackend(imageValue);
              console.log('S3ImageUpload: Got URL from backend for image property:', url);
              setCurrentImageUrl(url);
            } catch (err) {
              console.log('S3ImageUpload: Backend failed for image property, using local construction');
              // Fallback to local URL construction
              const url = s3Service.getFileUrl(imageValue);
              console.log('S3ImageUpload: Local URL construction for image property:', url);
              setCurrentImageUrl(url);
            }
          }
        }
      } catch (err) {
        console.error('S3ImageUpload: Error processing current image:', err);
        setCurrentImageUrl(null);
      }
    };

    processCurrentImage();
  }, [currentImage]);

  // Custom S3 upload helper with fallback (matching existing implementation)
  const uploadToS3 = async (file) => {
    try {
      // Method 1: Try presigned URL first (faster, direct to S3)
      console.log('Attempting presigned URL upload...');
      const presignRes = await fetch(`${process.env.REACT_APP_API_URL}/api/s3/presign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ 
          fileName: file.name, 
          fileType: file.type,
          folder: folder 
        }),
      });

      if (presignRes.ok) {
        const { url, key } = await presignRes.json();
        
        // Upload file to S3 using presigned URL with progress tracking
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          
          xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
              const percentComplete = (event.loaded / event.total) * 100;
              setUploadProgress(percentComplete);
            }
          });

          xhr.addEventListener("load", () => {
            if (xhr.status === 200) {
              console.log('Presigned URL upload successful');
              resolve(key);
            } else {
              reject(new Error(`Upload failed with status: ${xhr.status}`));
            }
          });

          xhr.addEventListener("error", () => {
            reject(new Error("Upload failed"));
          });

          xhr.addEventListener("abort", () => {
            reject(new Error("Upload aborted"));
          });

          xhr.open("PUT", url);
          xhr.send(file);
        });
      }

      throw new Error('Presigned URL upload failed, trying fallback...');

    } catch (presignError) {
      console.warn('Presigned URL upload failed:', presignError);

      try {
        // Method 2: Fallback to direct upload (slower, goes through backend)
        console.log('Attempting direct upload fallback...');
        const formData = new FormData();
        formData.append('file', file);
        
        const uploadRes = await fetch(`${process.env.REACT_APP_API_URL}/api/s3/upload`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        });

        if (uploadRes.ok) {
          const { key } = await uploadRes.json();
          console.log('Direct upload fallback successful');
          return key;
        } else {
          const errorData = await uploadRes.json();
          throw new Error(errorData.error || 'Direct upload failed');
        }

      } catch (directError) {
        console.error('Both upload methods failed:', directError);
        throw new Error(`Upload failed: ${directError.message}`);
      }
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError(null);
    setUploading(true);
    setUploadProgress(0);

    try {
      // Enhanced file type validation
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
      const fileExtension = file.name.toLowerCase().split('.').pop();
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
      
      // Check MIME type
      if (!file.type.startsWith('image/')) {
        throw new Error(`Invalid file type. Only image files are allowed. You selected: ${file.type || 'unknown type'}`);
      }
      
      // Check if it's a specific image type
      if (!allowedTypes.includes(file.type)) {
        throw new Error(`Unsupported image format. Please upload a JPEG, PNG, GIF, WebP, or BMP file. You selected: ${file.type}`);
      }
      
      // Check file extension
      if (!allowedExtensions.includes(fileExtension)) {
        throw new Error(`Invalid file extension. Please upload a file with one of these extensions: ${allowedExtensions.join(', ')}. You selected: ${fileExtension}`);
      }

      // Validate file size
      if (file.size > maxSize) {
        throw new Error(`File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB. Your file size is ${Math.round(file.size / (1024 * 1024))}MB`);
      }

      // Additional validation: Check if file is actually an image by trying to create an Image object
      const validateImage = () => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          const objectUrl = URL.createObjectURL(file);
          
          img.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(true);
          };
          
          img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('The selected file does not appear to be a valid image. Please try another file.'));
          };
          
          img.src = objectUrl;
        });
      };

      // Validate the image
      await validateImage();

      // Create preview immediately
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);

      // Upload to S3 using custom upload function
      const imageKey = await uploadToS3(file);

      // Call parent callbacks
      if (onImageUpload) {
        onImageUpload(imageKey, file);
      }
      if (onImageChange) {
        onImageChange(e);
      }

    } catch (err) {
      console.warn('S3 upload failed:', err);
      setError(err.message);
      setImagePreview(currentImageUrl); // Reset to current image on error
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Call parent callback with null to indicate empty image
      if (onImageUpload) {
        onImageUpload(null, null);
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setCurrentImageUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onImageUpload) {
      onImageUpload(null, null);
    }
  };

  // Determine which image to show
  const displayImage = imagePreview || currentImageUrl;

  return (
    <div className="s3-image-upload">
      <input
        ref={fileInputRef}
        type="file"
        className={className}
        onChange={handleImageChange}
        accept={accept}
        disabled={uploading}
      />
      
      {helpText && (
        <span className="help-block">{helpText}</span>
      )}

      {uploading && (
        <div className="alert alert-info mt-2">
          <i className="fas fa-spinner fa-spin me-2"></i>
          Uploading image... {Math.round(uploadProgress)}%
          {uploadProgress > 0 && (
            <div className="progress mt-2" style={{ height: '4px' }}>
              <div 
                className="progress-bar" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="alert alert-danger mt-2">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {showPreview && (
        <div className="mt-3">
          {displayImage ? (
            <div className="position-relative d-inline-block">
              <img
                src={displayImage}
                className={previewClassName}
                alt="Preview"
                style={{ width: '446px', height: '255px', objectFit: 'cover' }}
                onError={(e) => {
                  console.error('Image failed to load:', displayImage);
                  // Hide the broken image and show placeholder instead
                  e.target.style.display = 'none';
                  // Clear the preview to show example image
                  setImagePreview(null);
                  setCurrentImageUrl(null);
                }}
                onLoad={() => {
                  console.log('Image loaded successfully:', displayImage);
                }}
              />
              <button
                type="button"
                className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                onClick={handleRemoveImage}
                style={{ zIndex: 10 }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          ) : exampleImage ? (
            <div style={{ display: 'flex', alignSelf: 'center', alignContent: 'center', flexDirection: 'column' }}>
              The example:
              <img 
                src={exampleImage} 
                className={previewClassName} 
                alt="Example" 
                style={{ width: '446px', height: '255px', objectFit: 'cover' }}
              />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default S3ImageUpload; 